import React, { useState, useEffect, useRef } from "react"
import Video from '@bt-react/components/Video'
import { LocalVideoTrackPublication } from "twilio-video"
import styles from './VirtualBackgroundPicker.module.scss'
import Icon, { IconType } from '@bt-react/components/Icon'
import TooltipWrapper from "@bt-react/components/TooltipWrapper"
import { ReactComponent as BackgroundDark } from './dark-bg-no-user-button-001.svg'
import { ReactComponent as BackgroundBlurFull } from './full-blur-no-user-button-001.svg'
import { ReactComponent as BackgroundBlurLight } from './light-blur-no-user-button-001.svg'
import { get100FromCustomImage } from "../../lib/customVirtualBackground"
import classnames from 'classnames'


export interface VirtualBackgroundPickerProps {
    videoTrack: any
    onBackgroundSelect: (VirtualBackgrounds: VirtualBackgrounds) => void
    presenterBackground: PresenterBackground
    selectedBackgroundType?: VirtualBackgrounds
    onFileSelect?: (file: File) => void
}

export interface PresenterBackground {
    type: VirtualBackgrounds,
    customImage: string | null
  }

export enum VirtualBackgrounds {
    NONE = 'none',
    BLUR_HIGH = 'blur-high',
    BLUR_LOW = 'blur-low',
    SOLID_COLOR = 'solid-color',
    IMAGE = 'image'
} 



const VirtualBackgroundPicker = ({ videoTrack, onBackgroundSelect, presenterBackground, onFileSelect }: VirtualBackgroundPickerProps) => {

    const [selectedType, setSelectedType] = useState<VirtualBackgrounds | null>(null)
    const [fileLoading, setFileLoading] = useState<boolean>(false)
    const [hasFileSizeError, setHasFileSizeError] = useState<boolean>(false)
    const { type, customImage } = presenterBackground
    const prevProps = useRef({ customImage }).current

    useEffect(() => {
        if (customImage !== null && prevProps.customImage !== customImage) {
            setSelectedType(VirtualBackgrounds.IMAGE)
            onBackgroundSelect(VirtualBackgrounds.IMAGE)
            setFileLoading(false)
        }
        return () => { 
            prevProps.customImage = customImage
          }
    }, [customImage, onBackgroundSelect, prevProps])

    const onBackgroundClick = (background: VirtualBackgrounds) => {
        if (!fileLoading) {
            setSelectedType(background)
            onBackgroundSelect(background)
        }
    }

    const imageFileSelected = (file: File) => {
        if (file.size/1000/1000 > 5) {
            setHasFileSizeError(true)
            return
        }
        if (hasFileSizeError) setHasFileSizeError(false)
        if (onFileSelect) onFileSelect(file)
        setFileLoading(true)
    }

      return <div className={styles['container']}>
          {videoTrack && <Video
                    publication={{
                        track: videoTrack,
                        trackName: 'videofeed',
                        trackSid: `videofeed`
                    } as unknown as LocalVideoTrackPublication}
                    mirrorVideo={true}
                    trackSid={`videofeed`}
                    />}
    <div>
        <div>Select background:</div>
        <div className={
            classnames(styles['background-options'], {
                [styles['loading']]: fileLoading
            })}>

            <TooltipWrapper tooltipText='Light blur' position='top'>
                <div data-bdd='background-option-light-blur' className={
                    classnames(styles['background-option'], {
                        [styles['active-background']]: (selectedType || type) === VirtualBackgrounds.BLUR_LOW
                    })} 
                    onClick={() => onBackgroundClick(VirtualBackgrounds.BLUR_LOW)}>
                    <div><Icon className={styles['background-icon']} name={IconType.iconName.user} /><BackgroundBlurLight className={styles['background-icon-blur']} /></div>
                </div>
            </TooltipWrapper>
            <TooltipWrapper tooltipText='High blur' position='top'>
                <div data-bdd='background-option-high-blur' className={
                    classnames(styles['background-option'], {
                        [styles['active-background']]: (selectedType || type) === VirtualBackgrounds.BLUR_HIGH
                    })} 
                    onClick={() => onBackgroundClick(VirtualBackgrounds.BLUR_HIGH)}>
                    <div><Icon className={styles['background-icon']} name={IconType.iconName.user} /><BackgroundBlurFull className={styles['background-icon-blur']} /></div>
                </div>
            </TooltipWrapper>
            <TooltipWrapper tooltipText='Dark background' position='top'>
                <div data-bdd='background-option-dark' className={
                    classnames([styles['background-option'], styles['background-option-solid-background-wrapper']], {
                        [styles['active-background']]: (selectedType || type) === VirtualBackgrounds.SOLID_COLOR
                    })} 
                    onClick={() => onBackgroundClick(VirtualBackgrounds.SOLID_COLOR)}>
                    <div><Icon className={`${styles['background-icon']} ${styles['background-icon-solid-background']}`} name={IconType.iconName.user} /></div>
                </div>
            </TooltipWrapper>
            <TooltipWrapper tooltipText='No background' position='top'>
                <div data-bdd='background-option-none' className={
                    classnames(styles['background-option'], {
                        [styles['active-background']]: (selectedType || type) === VirtualBackgrounds.NONE
                    })} 
                    onClick={() => onBackgroundClick(VirtualBackgrounds.NONE)}>
                    <div><Icon className={styles['background-icon']} name={IconType.iconName.ban} /></div>
                </div>
            </TooltipWrapper>
            <TooltipWrapper tooltipText='Upload image (5MB max)' position='top'>
                <div data-bdd='background-option-upload' className={classnames([styles['background-option'], styles['upload-button']])}>
                    {/*@ts-ignore*/}
                    <div><Icon className={styles['background-icon']} name={fileLoading ? IconType.iconName.spinner : IconType.iconName.uploadCloud} />{fileLoading ? '' : <input onChange={({target}) => imageFileSelected(target.files[0])} type='file' accept="image/png, image/jpg, image/jpeg"/>}</div>
                </div>
            </TooltipWrapper>
            {customImage ? <div data-bdd='background-option-custom-image' className={
                    classnames(styles['background-option'], {
                        [styles['active-background']]: (selectedType || type) === VirtualBackgrounds.IMAGE
                    })}  
                    onClick={() => onBackgroundClick(VirtualBackgrounds.IMAGE)}>
                <div><img width={97} height={52} alt='background' src={get100FromCustomImage(customImage)}/></div>
            </div> : ''}
        </div>
        {hasFileSizeError && <div className={styles['file-size-error']}>Image file size is too large (5MB max)</div>}
    </div>
          </div>
}

export default VirtualBackgroundPicker